/*
  RateWatcher tracks the rate of a certain function/codepath over a period of time, reporting the ratio
  as a metric that can be watched.

  Motivation: We often send metrics like "duration of X" and "count of X", which lack the ability to tell us
  "What's the rate of impact on a single customer". By tracking these numbers over a consistent time window,
  we can understand if it translates to 1% of the time or 50% of the time.
*/

import {createMetricRollupSumProcessor, gauge, getMetricsQueue, MetricType} from 'metrics/Metrics';
import lazyInit from 'util/lazyInit';
import uniqueId from 'util/uniqueId';

const watchedRates = new Map<string, RateResult>();

export const RATE_ID_DISABLED = Symbol('RateDisabled');
export type RateId = string | typeof RATE_ID_DISABLED;

interface RateResult {
  name: string;
  rateId: RateId;
  lastSent: number;
  currentDuration: number;
  currentCount: number;
  intervalId: NodeJS.Timeout | null;
  tags: {[key: string]: string | number | boolean};
}

function emitRateMetric(rateId: RateId): void {
  if (rateId === RATE_ID_DISABLED) {
    return;
  }
  const result = watchedRates.get(rateId);
  if (!result || result.currentCount === 0) {
    return;
  }

  const duration = result.currentDuration;
  const count = result.currentCount;
  const lastSent = result.lastSent;
  result.currentDuration = 0;
  result.currentCount = 0;
  result.lastSent = Date.now();

  const totalDuration = Date.now() - lastSent;
  const ratio = duration / totalDuration;
  gauge(`${result.name}.percent`, ratio, result.tags);
  gauge(`${result.name}.count`, count, result.tags);
}

export function recordDuration(rateId: RateId, duration: number): void {
  if (rateId === RATE_ID_DISABLED) {
    return;
  }
  const result = watchedRates.get(rateId);
  if (!result) {
    return;
  }
  result.currentDuration += duration;
  result.currentCount++;
}

const addedPreprocessorPrefixes: () => Array<string> = lazyInit(() => []);

// setup metric preprocessors for a specific prefix to rollup any metrics starting with that prefix
export function addRatePreprocessors(prefix: string): void {
  if (addedPreprocessorPrefixes().includes(prefix)) {
    return;
  }
  const mq = getMetricsQueue();
  mq.addPreprocessor(createMetricRollupSumProcessor(prefix, MetricType.GAUGE, 'percent'));
  mq.addPreprocessor(createMetricRollupSumProcessor(prefix, MetricType.GAUGE, 'count'));
  addedPreprocessorPrefixes().push(prefix);
}

export function createNewRate(
  name: string,
  tags: {[key: string]: string | number | boolean},
  intervalDuration: number
): RateId {
  const rateId = uniqueId(`${name}_`);
  const emitter = emitRateMetric.bind(null, rateId);

  const result: RateResult = {
    name,
    rateId,
    lastSent: Date.now(),
    currentDuration: 0,
    currentCount: 0,
    intervalId: setInterval(emitter, intervalDuration),
    tags,
  };

  watchedRates.set(rateId, result);
  return rateId;
}

export function disableRate(rateId: RateId): void {
  if (rateId === RATE_ID_DISABLED) {
    return;
  }
  const result = watchedRates.get(rateId);
  if (!result || !result.intervalId) {
    return;
  }
  clearInterval(result.intervalId);
  result.intervalId = null;
  watchedRates.delete(rateId);
}
